/**
 * Subscription Details Card
 * 
 * Displays user's active subscription (membership or pass) information.
 * Shows subscription name, type, and coverage status.
 * 
 * @module butterUI/components/SubscriptionDetailsCard
 */


export interface SubscriptionDetailsCardProps {
  /** Subscription type */
  type: 'membership' | 'pass';
  /** Subscription name (e.g., "Gold Individual", "10-Visit Pass") */
  name: string;
  /** Optional CSS class name */
  className?: string;
}

/**
 * Subscription Details Card Component
 * 
 * Displays active subscription information with icon and coverage status.
 * Follows mobile-first responsive design and WCAG 2.1 AA accessibility.
 * 
 * @param type - The type of subscription (membership or pass)
 * @param name - The name of the subscription plan
 * @param className - Optional CSS class name for styling
 * 
 * @example
 * ```tsx
 * <SubscriptionDetailsCard
 *   type="membership"
 *   name="Gold Individual"
 * />
 * ```
 * 
 * @example
 * ```tsx
 * <SubscriptionDetailsCard
 *   type="pass"
 *   name="10-Visit Pass"
 * />
 * ```
 */
export function SubscriptionDetailsCard({
  type,
  name,
  className = '',
}: SubscriptionDetailsCardProps) {
  const iconLabel = type === 'membership' ? 'Membership' : 'Pass';
  
  return (
    <div 
      className={`rounded-2xl border border-theme-200 p-6 ${className}`}
      role="region"
      aria-label="Subscription details"
      data-testid="subscription-details-card"
      // Which kind of plan pays — the booking's payer, which for a pair holder can be
      // their pass rather than their membership (pass-first, subscription unification D13).
      data-plan-type={type}
    >
      <h3 className="mb-4 text-sm font-normal text-gray-500">
        Active {iconLabel}
      </h3>
      
      <div className="flex items-start justify-between gap-4">
        {/* Left: Icon + Name */}
        <div className="flex items-center gap-3">
          <div>
            <p className="text-base font-medium text-theme-600">
              {name}
            </p>
            <p className='text-sm text-fg-quaternary mt-4'>You can book using your {iconLabel} benefits.</p>
            <p className='text-sm text-fg-quaternary'>No payment required for this booking unless an overage occurs.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
